import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const SECTIONS = [
  { id: "identification", key: "sec_identification" },
  { id: "measures", key: "sec_measures" },
  { id: "health", key: "sec_health" },
  { id: "allergies", key: "sec_allergies" },
  { id: "meds", key: "sec_meds" },
  { id: "prescription", key: "sec_prescription" },
  { id: "contacts", key: "sec_contacts" },
];

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#F0F5FA] text-sm">
      <span className="text-[#627D98]">{label}</span>
      <span className="text-[#0D253F] font-medium">{value ?? "—"}</span>
    </div>
  );
}

function age(birth) {
  if (!birth) return null;
  const d = new Date(birth);
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
}

export default function HealthRecord() {
  const { t } = useI18n();
  const { activeProfileId, activeProfile, can } = useProfile();
  const [tab, setTab] = useState("identification");
  const [measures, setMeasures] = useState([]);
  const [prescription, setPrescription] = useState(null);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const [m, p] = await Promise.all([
      api.get(`/profiles/${activeProfileId}/measures`),
      api.get(`/profiles/${activeProfileId}/prescription`),
    ]);
    setMeasures(m.data);
    setPrescription(p.data.current);
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  async function addMeasure(kind, value) {
    const v = parseFloat(String(value).replace(",", "."));
    if (Number.isNaN(v) || v <= 0) { toast.error("Valor inválido."); return; }
    await api.post(`/profiles/${activeProfileId}/measures`, { kind, value: v, date: new Date().toISOString().slice(0, 10) });
    toast.success(t("save"));
    setWeight(""); setHeight("");
    load();
  }

  const lastWeight = measures.find((m) => m.kind === "weight");
  const lastHeight = measures.find((m) => m.kind === "height");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("record_title")}</h1>
        <p className="text-[#627D98] mt-1">{t("record_sub")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setTab(s.id)} data-testid={`record-tab-${s.id}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${tab === s.id ? "bg-[#1A73E8] text-white" : "bg-white border border-[#E3EFF9] text-[#3B526B]"}`}>
            {t(s.key)}
          </button>
        ))}
      </div>

      <div className="df-card p-5">
        {tab === "identification" && (
          <div>
            <Row label={t("onb_name")} value={activeProfile?.preferred_name} />
            <Row label={t("birth_date")} value={activeProfile?.birth_date} />
            <Row label={t("age")} value={age(activeProfile?.birth_date) != null ? `${age(activeProfile?.birth_date)} ${t("years")}` : null} />
            <Row label={t("onb_country")} value={activeProfile?.country} />
            <Row label={t("onb_unit")} value={activeProfile?.glucose_unit} />
          </div>
        )}

        {tab === "measures" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#627D98]">{t("weight")}</Label>
                <div className="flex gap-2">
                  <Input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="measure-weight" />
                  {can("log_measures") && <Button onClick={() => addMeasure("weight", weight)} className="bg-[#1A73E8] text-white" data-testid="save-weight">+</Button>}
                </div>
                {lastWeight && <p className="text-xs text-[#627D98] mt-1">Atual: {lastWeight.value} kg ({lastWeight.date})</p>}
              </div>
              <div>
                <Label className="text-xs text-[#627D98]">{t("height")}</Label>
                <div className="flex gap-2">
                  <Input inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} data-testid="measure-height" />
                  {can("log_measures") && <Button onClick={() => addMeasure("height", height)} className="bg-[#1A73E8] text-white" data-testid="save-height">+</Button>}
                </div>
                {lastHeight && <p className="text-xs text-[#627D98] mt-1">Atual: {lastHeight.value} cm ({lastHeight.date})</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "health" && (
          <div>
            <Row label={t("diabetes_type")} value={activeProfile?.diabetes_type} />
            <Row label={t("diagnosis_year")} value={activeProfile?.diagnosis_year} />
            <p className="text-xs text-[#627D98] mt-3 flex items-start gap-1.5"><Info size={13} className="mt-0.5" /> {t("no_diagnosis")}</p>
          </div>
        )}

        {tab === "allergies" && (
          <div>
            <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-[#EBF3FA] text-[#3B526B]" data-testid="allergy-state">{t("allergy_state_unknown")}</div>
            <p className="text-sm text-[#627D98] mt-3">Estado explícito: não informado / nenhuma conhecida confirmada / registos existentes.</p>
          </div>
        )}

        {tab === "meds" && (
          <p className="text-sm text-[#627D98]">Medicações, insulinas e dispositivos — a transcrever conforme prescrição.</p>
        )}

        {tab === "prescription" && (
          <div>
            <Row label={t("fsi")} value={prescription?.fsi} />
            <Row label={t("ratio_ihc")} value={prescription?.ratio_ihc ? `1 U : ${prescription.ratio_ihc} g` : null} />
            <Row label={t("correction_target")} value={prescription?.correction_target} />
            <div className="mt-3">
              {prescription?.confirmed_by ? (
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-[#E6F4EA] text-[#137333]">{t("prescription_confirmed")}</span>
              ) : (
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-[#FEF7E0] text-[#8a5a00]">Por confirmar</span>
              )}
            </div>
            <p className="text-xs text-[#627D98] mt-3">{t("not_clinical_validation")} <a href="/app/definicoes" className="text-[#1A73E8] font-medium">{t("nav_settings")}</a></p>
          </div>
        )}

        {tab === "contacts" && (
          <p className="text-sm text-[#627D98]">Responsável e cuidadores autorizados em <a href="/app/familia" className="text-[#1A73E8] font-medium">{t("nav_family")}</a>. Guardar contactos não autoriza envio de mensagens.</p>
        )}
      </div>
    </div>
  );
}
